/* Azar Group — photo swiper on product cards (snippets/card-product.liquid).
   The track is a CSS scroll-snap strip, so a phone swipes it with no script at
   all. This adds the arrows and dots, mouse dragging on a computer, and loads
   the later photos only when the shopper is about to see them. */
(() => {
  if (customElements.get('azar-card-gallery')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Later photos carry data-srcset/data-src until they are needed.
  function load(img) {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.alt = img.dataset.alt || '';
    delete img.dataset.srcset;
    delete img.dataset.src;
    delete img.dataset.alt;
  }

  // A card coming into view fetches its second photo, so the first swipe is instant.
  const nearView =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              nearView.unobserve(entry.target);
              entry.target.prime();
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  class AzarCardGallery extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      if (!this.track) return;
      if (!this.primed) {
        if (nearView) nearView.observe(this);
        else this.prime();
      }
    }

    disconnectedCallback() {
      if (nearView) nearView.unobserve(this);
    }

    setup() {
      this.track = this.querySelector('.azar-gallery__track');
      if (!this.track) return;
      this.ready = true;
      this.slides = Array.from(this.track.children);
      this.dots = Array.from(this.querySelectorAll('.azar-gallery__dot'));
      this.prev = this.querySelector('.azar-gallery__arrow--prev');
      this.next = this.querySelector('.azar-gallery__arrow--next');
      this.index = 0;
      this.frame = 0;

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.update();
          });
        },
        { passive: true }
      );

      // Also once the glide has stopped, in case a frame was skipped.
      this.track.addEventListener('scrollend', () => this.update());

      // A second click during the glide counts from where the first one is heading.
      const step = (by) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.loadAll();
        const heading = this.heading && performance.now() - this.heading.at < 700;
        this.go((heading ? this.heading.to : this.position()) + by);
      };
      if (this.prev) this.prev.addEventListener('click', step(-1));
      if (this.next) this.next.addEventListener('click', step(1));

      // Any sign of interest loads the rest, so a quick swipe never lands on a blank.
      const wake = () => this.loadAll();
      ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
        this.addEventListener(type, wake, { once: true, passive: true })
      );

      // A swipe or press between arrow clicks wins over the remembered destination.
      this.track.addEventListener(
        'touchstart',
        () => {
          this.heading = null;
        },
        { passive: true }
      );
      this.track.addEventListener('pointerdown', (event) => {
        this.heading = null;
        this.drag(event);
      });
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      // A drag with the mouse must not also open the product.
      this.track.addEventListener(
        'click',
        (event) => {
          if (!this.swallowClick) return;
          this.swallowClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      this.update();
    }

    prime() {
      this.primed = true;
      if (this.slides && this.slides[1]) load(this.slides[1].querySelector('img'));
    }

    loadAll() {
      this.primed = true;
      if (nearView) nearView.unobserve(this);
      (this.slides || []).forEach((slide) => load(slide.querySelector('img')));
    }

    // +1 when the strip scrolls left to right, -1 on a right-to-left page.
    get sign() {
      return getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    }

    position() {
      const width = this.track.clientWidth;
      if (!width) return 0;
      const at = Math.round(Math.abs(this.track.scrollLeft) / width);
      return Math.max(0, Math.min(this.slides.length - 1, at));
    }

    update() {
      const at = this.position();
      this.index = at;
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === at));
      if (this.prev) this.prev.hidden = at === 0;
      if (this.next) this.next.hidden = at >= this.slides.length - 1;
    }

    // Returns the scroll position it heads for.
    go(target) {
      const width = this.track.clientWidth;
      if (!width) return this.track.scrollLeft;
      const to = Math.max(0, Math.min(this.slides.length - 1, target));
      const left = this.sign * to * width;
      this.heading = { to, at: performance.now() };
      this.track.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return left;
    }

    // Mouse only: fingers and trackpads already scroll the strip natively.
    drag(down) {
      if (down.pointerType !== 'mouse' || down.button !== 0) return;
      const width = this.track.clientWidth;
      if (!width) return;
      const startX = down.clientX;
      const startLeft = this.track.scrollLeft;
      const from = this.position();
      let moved = false;

      const move = (event) => {
        // Released outside the strip before the drag began: stop listening.
        if (!(event.buttons & 1)) {
          end(event);
          return;
        }
        const dx = event.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) < 6) return;
          moved = true;
          this.loadAll();
          this.classList.add('is-dragging');
          try {
            this.track.setPointerCapture(down.pointerId);
          } catch (error) {
            /* the pointer is already gone */
          }
        }
        this.track.scrollLeft = startLeft - dx;
      };

      const end = (event) => {
        this.track.removeEventListener('pointermove', move);
        this.track.removeEventListener('pointerup', end);
        this.track.removeEventListener('pointercancel', end);
        if (!moved) return;
        this.swallowClick = true;
        setTimeout(() => {
          this.swallowClick = false;
        }, 0);
        const dx = event.clientX - startX;
        let to = from;
        // A short, deliberate pull is enough to move one photo.
        if (Math.abs(dx) > width * 0.15) to = from + (dx < 0 ? 1 : -1) * this.sign;
        const left = this.go(to);
        this.whenSettled(left, () => this.classList.remove('is-dragging'));
      };

      this.track.addEventListener('pointermove', move);
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
    }

    // Snapping stays off until the glide to the chosen photo has finished,
    // otherwise the browser snaps back to the nearest photo mid-glide.
    whenSettled(left, done) {
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        this.track.removeEventListener('scrollend', arrived);
        done();
      };
      // A late scrollend from the drag itself must not end the glide early.
      const arrived = () => {
        if (Math.abs(this.track.scrollLeft - left) < 2) finish();
      };
      this.track.addEventListener('scrollend', arrived);
      setTimeout(finish, 800);
    }
  }

  customElements.define('azar-card-gallery', AzarCardGallery);
})();
